import { TFunction } from 'i18next';
import { omit, sumBy } from 'lodash';
import { Countries } from 'shared/enums';
import { formatNumber } from 'shared/utils';
import XLSX from 'sheetjs-style';
import { CountryData, ParametersFormData } from 'store/countriesDataStore';
import { EMPTY_VALUE } from 'shared/constants';
import { footerStyles, headerStyles } from './constants';
import getBudgetRow from './getBudgetRow';
import generateData from './generateData';
import { Currencies } from '../../shared/types';
import formatNumberOrDash from './helpers/formatNumberOrDash';

export default (
  countryData: CountryData,
  formData: ParametersFormData,
  t: TFunction<'translation', undefined, 'translation'>,
  country: Countries,
  countryCurrency: Currencies,
  exchangeRates: any
) => {
  const filteredData = countryData.estimatedBudget.filter((x) => {
    if (x.isMustHave) return countryData.mustHaveIds?.[x.id] === true;
    return countryData.optionalIds?.[x.id] === true;
  });

  const firstWhitespacesCount = 3;
  const secondWhitespacesCount = 9;

  const ws = generateData(
    countryData,
    filteredData,
    formData,
    country,
    firstWhitespacesCount,
    secondWhitespacesCount,
    t,
    countryCurrency,
    exchangeRates
  );

  const exchangeRate =
    countryCurrency !== 'EUR'
      ? exchangeRates?.data?.exchangeRates?.find((rate: any) =>
          rate.name.toLowerCase().includes(countryCurrency.toLowerCase())
        )?.value
      : null;

  const mediaColumns = ['impressions', 'reach', 'clicks', 'videoViews'];
  const footerStart = filteredData.length + 2 + 2 + firstWhitespacesCount;

  // ✅ If you updated getBudgetRow to additionally filter by ids, pass them in:
  const budgetMustHave = getBudgetRow(
    filteredData,
    true,
    mediaColumns,
    t,
    countryCurrency,
    exchangeRates,
    countryData.mustHaveIds,
    countryData.optionalIds
  );

  const budgetOptional = getBudgetRow(
    filteredData,
    false,
    mediaColumns,
    t,
    countryCurrency,
    exchangeRates,
    countryData.mustHaveIds,
    countryData.optionalIds
  );

  const budgetTotal = [
    t('totalMedia'),
    formatNumberOrDash(sumBy(filteredData, 'budget')),
    exchangeRate
      ? formatNumberOrDash(sumBy(filteredData, 'budget') * exchangeRate)
      : 'N/A',
    formatNumber(sumBy(filteredData, 'impressions')),
    EMPTY_VALUE,
    formatNumber(sumBy(filteredData, 'clicks')),
    formatNumber(sumBy(filteredData, 'videoViews')),
    EMPTY_VALUE,
  ];

  const budgetEstTechCosts = [
    t('estimatedCosts'),
    formatNumberOrDash(sumBy(filteredData, 'techCost')),
    exchangeRate
      ? formatNumberOrDash(sumBy(filteredData, 'techCost') * exchangeRate)
      : 'N/A',
    EMPTY_VALUE,
    EMPTY_VALUE,
    EMPTY_VALUE,
    EMPTY_VALUE,
    EMPTY_VALUE,
  ];

  XLSX.utils.sheet_add_aoa(
    ws,
    [budgetMustHave, budgetOptional, budgetTotal, budgetEstTechCosts],
    {
      origin: `C${footerStart}`,
    }
  );

  ws['!cols'] = [
    { wpx: 300 }, // Touchpoint
    { wpx: 200 }, // Touchpoint Funnel
    { wpx: 200 }, // Is Must Have
    { wpx: 200 }, // Budget
    { wpx: 200 }, // Converted Budget
    { wpx: 200 }, // Impressions
    { wpx: 100 }, // Reach
    { wpx: 100 }, // Clicks
    { wpx: 100 }, // Video Views
    { wpx: 100 }, // Frequency
  ];

  // footer - budget column styles
  ws[`D${footerStart}`].s = { alignment: { horizontal: 'left' } };
  ws[`D${footerStart + 1}`].s = { alignment: { horizontal: 'left' } };
  ws[`D${footerStart + 2}`].s = { alignment: { horizontal: 'left' } };
  ws[`D${footerStart + 3}`].s = { alignment: { horizontal: 'left' } };

  ws[`E${footerStart}`].s = { alignment: { horizontal: 'left' } };
  ws[`E${footerStart + 1}`].s = { alignment: { horizontal: 'left' } };
  ws[`E${footerStart + 2}`].s = { alignment: { horizontal: 'left' } };
  ws[`E${footerStart + 3}`].s = { alignment: { horizontal: 'left' } };

  const estimatedBudgetColumnLength =
    filteredData.length > 0
      ? Object.keys(omit(filteredData[0], ['id', 'techCost'])).length
      : 0;

  for (let i = 0; i <= estimatedBudgetColumnLength; i++) {
    const column = String.fromCharCode(65 + i);

    if (column === 'D') {
      ws[`${column}${footerStart - 1}`].s = {
        border: { ...footerStyles.border },
        alignment: { horizontal: 'left' },
      };
    } else if (column === 'E') {
      ws[`${column}${footerStart - 1}`].s = {
        border: { ...footerStyles.border },
        alignment: { horizontal: 'left' },
      };
    } else if (column === 'C') {
      ws[`${column}${footerStart - 1}`].s = {
        ...footerStyles,
        font: {
          color: ws[`C${footerStart - 1}`].v?.includes?.('Optional')
            ? { rgb: '2F80ED' }
            : { rgb: '8043B9' },
        },
      };
    } else {
      ws[`${column}${footerStart - 1}`].s = footerStyles;
    }
  }

  const styleFooterLabelAndBudget = (color: string, index: number) => {
    ws[`C${footerStart + index}`].s = { font: { color: { rgb: color } } };
    ws[`D${footerStart + index}`].s = {
      font: { color: { rgb: color } },
      alignment: { horizontal: 'left' },
    };
  };

  styleFooterLabelAndBudget('8043B9', 0);
  styleFooterLabelAndBudget('2F80ED', 1);
  styleFooterLabelAndBudget('3B455E', 3);

  // style parameters table
  ws[`A${footerStart + secondWhitespacesCount}`].s = headerStyles;
  ws[`B${footerStart + secondWhitespacesCount}`].s = headerStyles;
  ws[`C${footerStart + secondWhitespacesCount}`].s = headerStyles;
  ws[`D${footerStart + secondWhitespacesCount}`].s = headerStyles;
  ws[`E${footerStart + secondWhitespacesCount}`].s = headerStyles;

  const desiredRowHeight = 18;

  const totalRows = ws['!ref']
    ? parseInt(ws['!ref'].split(':')[1].replace(/[^0-9]/g, ''), 10) + 1
    : 100;

  ws['!rows'] = new Array(totalRows).fill({ hpt: desiredRowHeight });

  return ws;
};
